import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post, put } from "./api";
import type {
  Admin,
  ChatSession,
  Course,
  Group,
  Handoff,
  InstructionSet,
  InstructionVersion,
  KnowledgeDoc,
  Lead,
  Message,
  PendingHandoffCount,
  PlacementRule,
} from "./types";

export const queryKeys = {
  leads: ["leads"] as const,
  lead: (id: string) => ["leads", id] as const,
  leadMessages: (id: string) => ["leads", id, "messages"] as const,
  handoffs: ["handoffs"] as const,
  handoff: (id: string) => ["handoffs", id] as const,
  pendingCount: ["handoffs", "pending-count"] as const,
  courses: ["courses"] as const,
  groups: ["groups"] as const,
  rules: ["rules"] as const,
  knowledge: ["knowledge"] as const,
  instructions: ["instructions"] as const,
  instructionVersions: (id: string) => ["instructions", id, "versions"] as const,
  config: ["config"] as const,
  placementAssessments: ["placements", "assessments"] as const,
  admins: ["admins"] as const,
  chatMessages: (id: string) => ["chat", id, "messages"] as const,
};

// Leads
export function useLeads() {
  return useQuery({ queryKey: queryKeys.leads, queryFn: () => get<Lead[]>("/api/leads") });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.lead(id ?? ""),
    queryFn: async () => {
      const payload = await get<{ lead: Lead } | Lead>(`/api/leads/${id}`);
      return (payload as { lead: Lead }).lead ?? (payload as Lead);
    },
    enabled: Boolean(id),
  });
}

export function useLeadMessages(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.leadMessages(id ?? ""),
    queryFn: () => get<Message[]>(`/api/leads/${id}/messages`),
    enabled: Boolean(id),
  });
}

export function useStartAdminChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => post(`/api/leads/${leadId}/admin-chat/start`),
    onSuccess: (_, leadId) => {
      qc.invalidateQueries({ queryKey: queryKeys.lead(leadId) });
      qc.invalidateQueries({ queryKey: queryKeys.leadMessages(leadId) });
    },
  });
}

export function useEndAdminChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => post(`/api/leads/${leadId}/admin-chat/end`),
    onSuccess: (_, leadId) => {
      qc.invalidateQueries({ queryKey: queryKeys.lead(leadId) });
    },
  });
}

export function useSendAdminMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, body }: { leadId: string; body: string }) =>
      post(`/api/leads/${leadId}/admin-chat/send`, { body }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.leadMessages(vars.leadId) });
    },
  });
}

// Handoffs / Requests
export function useHandoffs() {
  return useQuery({
    queryKey: queryKeys.handoffs,
    queryFn: () => get<Handoff[]>("/api/handoffs"),
  });
}

export function usePendingHandoffCount() {
  return useQuery({
    queryKey: queryKeys.pendingCount,
    queryFn: () => get<PendingHandoffCount>("/api/requests/pending/count"),
    refetchInterval: 30_000,
  });
}

export function useHandoff(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.handoff(id ?? ""),
    queryFn: () => get<Handoff>(`/api/requests/${id}`),
    enabled: Boolean(id),
  });
}

export function useUpdateHandoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      put(`/api/requests/${id}`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.handoffs });
      qc.invalidateQueries({ queryKey: queryKeys.handoff(vars.id) });
      qc.invalidateQueries({ queryKey: queryKeys.pendingCount });
    },
  });
}

export function useAssignHandoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post(`/api/requests/${id}/assign`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.handoffs });
      qc.invalidateQueries({ queryKey: queryKeys.handoff(id) });
      qc.invalidateQueries({ queryKey: queryKeys.pendingCount });
    },
  });
}

// Courses
export function useCourses() {
  return useQuery({ queryKey: queryKeys.courses, queryFn: () => get<Course[]>("/api/courses") });
}

export function useSaveCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Partial<Course> }) =>
      id ? put<Course>(`/api/courses/${id}`, body) : post<Course>("/api/courses", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.courses }),
  });
}

export function useDeleteCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/api/courses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.courses }),
  });
}

// Groups
export function useGroups() {
  return useQuery({ queryKey: queryKeys.groups, queryFn: () => get<Group[]>("/api/groups") });
}

export function useSaveGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Partial<Group> }) =>
      id ? put<Group>(`/api/groups/${id}`, body) : post<Group>("/api/groups", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.groups }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/api/groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.groups }),
  });
}

export function useSyncGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ synced?: number }>("/api/groups/sync-external"),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.groups }),
  });
}

// Rules
export function useRules() {
  return useQuery({ queryKey: queryKeys.rules, queryFn: () => get<PlacementRule[]>("/api/placement-rules") });
}

export function useSaveRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<PlacementRule>) => post<PlacementRule>("/api/placement-rules", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.rules }),
  });
}

// Knowledge
export function useKnowledge() {
  return useQuery({ queryKey: queryKeys.knowledge, queryFn: () => get<KnowledgeDoc[]>("/api/knowledge") });
}

export function useSaveKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<KnowledgeDoc>) => post<KnowledgeDoc>("/api/knowledge", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.knowledge }),
  });
}

export function useDeleteKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/api/knowledge/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.knowledge }),
  });
}

export function useReindexKnowledge() {
  return useMutation({
    mutationFn: () => post<{ reindexed?: number }>("/api/knowledge/reindex"),
  });
}

// Instructions
export function useInstructions() {
  return useQuery({
    queryKey: queryKeys.instructions,
    queryFn: () => get<InstructionSet[]>("/api/instructions"),
  });
}

export function useInstructionVersions(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.instructionVersions(id ?? ""),
    queryFn: () => get<InstructionVersion[]>(`/api/instructions/${id}/versions`),
    enabled: Boolean(id),
  });
}

export function useSaveInstruction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Partial<InstructionSet> }) =>
      id ? put<InstructionSet>(`/api/instructions/${id}`, body) : post<InstructionSet>("/api/instructions", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.instructions }),
  });
}

// Config
export interface ConfigEntry {
  key: string;
  value: unknown;
}

export function useConfig() {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: () => get<ConfigEntry[]>("/api/config"),
  });
}

export function useSaveConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      post(`/api/config`, { key, value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.config }),
  });
}

export function usePlacementAssessments() {
  return useQuery({
    queryKey: queryKeys.placementAssessments,
    queryFn: async () => {
      const payload = await get<
        | { assessments: Array<{ id: string; name: string; subject?: string }> }
        | Array<{ id: string; name: string; subject?: string }>
      >("/api/placements/assessments");
      if (Array.isArray(payload)) return payload;
      return payload.assessments ?? [];
    },
  });
}

// Admins
export function useAdmins() {
  return useQuery({ queryKey: queryKeys.admins, queryFn: () => get<Admin[]>("/api/auth/admins") });
}

// Chat sessions
export function useCreateChatSession() {
  return useMutation({
    mutationFn: (body: { title?: string; lead_id?: string | null }) =>
      post<ChatSession>("/api/chat/sessions", body),
  });
}

export function useChatMessages(sessionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.chatMessages(sessionId ?? ""),
    queryFn: () => get<Message[]>(`/api/chat/sessions/${sessionId}/messages`),
    enabled: Boolean(sessionId),
  });
}

export function useSendChatMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: string }) =>
      post<Message>(`/api/chat/sessions/${sessionId}/messages`, { body }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.chatMessages(vars.sessionId) });
    },
  });
}
